import { useEffect, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button, Row } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';
import { TIndividualCustomer } from 'utils/types';
import DataTable from '../BranchProductInventory/DataTable';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import { PurchaseOrderHandle } from '../PurchaseOrder/CreatePurchaseOrderForm';
import CreateIndividualForm from './CreateIndividualForm';
import { Link } from 'react-router-dom';

const IndividualCustomerList = () => {
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addIndividualCustomerFormRef = useRef<PurchaseOrderHandle>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(`/api/v1/customers/individuals/`, {
      params: {
        page,
        pageSize,
        search,
        orderBy,
        orderByDesc
      }
    });
    return data;
  };

  const createIndividualCustomer = () => {
    setModalProps({
      title: 'Create Customer',
      content: <CreateIndividualForm ref={addIndividualCustomerFormRef} />,
      onSubmit: () => handleSubmitIndividualCustomer(),
      onCancel: () => modalRef.current.hide()
    });
    modalRef.current.show();
  };

  const handleSubmitIndividualCustomer = async () => {
    if (addIndividualCustomerFormRef.current) {
      try {
        const isSuccess = await addIndividualCustomerFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const columns: ColumnDef<TIndividualCustomer>[] = [
    {
      id: 'firstName',
      header: 'First name',
      accessorKey: 'firstName',
      enableSorting: false
    },
    {
      id: 'lastName',
      header: 'Last name',
      accessorKey: 'lastName',
      enableSorting: false
    },
    {
      id: 'username',
      header: 'Username',
      accessorKey: 'email',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <Link to={`mailto:${original.email || original.contactNumber}`}>
          {original.email || original.contactNumber}
        </Link>
      )
    },
    {
      id: 'membershipNumber',
      header: 'Membership Number',
      accessorKey: 'membershipNumber',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link
            to={`/customer-details/${original.customerId}`}
            style={{
              cursor: 'pointer',
              marginRight: '.5rem',
              textDecoration: 'none'
            }}
          >
            {original.membershipNumber}
          </Link>
        );
      }
    },
    {
      id: 'profession',
      header: 'Profession',
      accessorKey: 'profession',
      enableSorting: false
    },
    {
      id: 'workCompanyName',
      header: 'Work CompanyName',
      accessorKey: 'workCompanyName',
      enableSorting: false
    }
  ];

  useEffect(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  return (
    <>
      <Row lg="1" className="mb-3">
        <h4>Customer List</h4>
      </Row>
      <DataTable
        queryKey={['individualCustomer', isSubmitted]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
          <>
            <Row lg="auto">
              <Button
                variant="primary"
                className="w-10 mb-3"
                onClick={createIndividualCustomer}
              >
                Create Customer
              </Button>
            </Row>
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default IndividualCustomerList;